import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '../../auth/useUser';
import { useGenerator } from './context/GeneratorContext';
import SignupPrompt from '../../components/ui/SignupPrompt';
import VerifyEmailModal from '../../components/ui/VerifyEmailModal';

const DIMENSION_OPTIONS = {
  ios: [
    // Minimal, high-signal set for development today
    { size: '1024x1024', label: 'App Store', description: 'Required for App Store submission' },
    { size: '180x180', label: 'iPhone', description: 'Home screen icon (@3x)' },
  ],
  android: [
    // Minimal, high-signal set for development today
    { size: '512x512', label: 'Play Store', description: 'Required for Play Store listing' },
    { size: '192x192', label: 'Launcher', description: 'Common launcher icon size' },
  ],
  web: [
    // Minimal, high-signal set for development today
    { size: '512x512', label: 'PWA Large', description: 'Web app manifest icon' },
    { size: '192x192', label: 'PWA Small', description: 'Web app manifest icon' },
    { size: '32x32', label: 'Favicon', description: 'Browser tab icon' },
    { size: '16x16', label: 'Favicon Small', description: 'Browser tab icon' },
  ],
};

const DOWNLOAD_PLATFORMS = [
  { id: 'web', label: 'Web', description: 'Favicons + PWA icons' },
  { id: 'ios', label: 'iOS', description: 'App icons for Xcode projects' },
  { id: 'android', label: 'Android', description: 'Launcher icons for Android projects' },
];

const DEFAULT_ENABLED = { web: true, ios: true, android: true };

const DEFAULT_SIZES = {
  web: DIMENSION_OPTIONS.web.map(d => d.size),
  ios: DIMENSION_OPTIONS.ios.map(d => d.size),
  android: DIMENSION_OPTIONS.android.map(d => d.size),
};

export default function FinalizeView({
  logo,
  onBack,
  onDownload,
}) {
  const navigate = useNavigate();
  useAuth0();
  const { isAuthenticated, user, credits, downloadCost, canDownload: hasEnoughCredits } = useUser();
  const { actions } = useGenerator();
  const [enabledPlatforms, setEnabledPlatforms] = useState(DEFAULT_ENABLED);
  const [selectedSizesByPlatform, setSelectedSizesByPlatform] = useState(DEFAULT_SIZES);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);

  const togglePlatform = (platformId) => {
    setEnabledPlatforms(prev => {
      const nextEnabled = !prev[platformId];
      // If enabling and there are no sizes selected (e.g., previously cleared), restore defaults synchronously
      if (nextEnabled) {
        setSelectedSizesByPlatform(prevSizes => {
          const current = prevSizes[platformId] || [];
          if (current.length > 0) return prevSizes;
          return { ...prevSizes, [platformId]: DEFAULT_SIZES[platformId] || [] };
        });
      }
      return { ...prev, [platformId]: nextEnabled };
    });
  };

  const toggleSize = (platformId, size) => {
    setSelectedSizesByPlatform(prev => {
      const current = prev[platformId] || [];
      return {
        ...prev,
        [platformId]: current.includes(size)
          ? current.filter(s => s !== size)
          : [...current, size],
      };
    });
  };

  const selectAll = (platformId) => {
    setSelectedSizesByPlatform(prev => ({
      ...prev,
      [platformId]: (DIMENSION_OPTIONS[platformId] || []).map(d => d.size),
    }));
  };

  const selectNone = (platformId) => {
    setSelectedSizesByPlatform(prev => ({
      ...prev,
      [platformId]: [],
    }));
  };

  const targets = DOWNLOAD_PLATFORMS
    .filter(p => enabledPlatforms[p.id])
    .map(p => ({
      platform: p.id,
      sizes: selectedSizesByPlatform[p.id] || [],
    }));

  const totalSelectedFiles = targets.reduce((sum, t) => sum + (t.sizes?.length || 0), 0);
  const canDownload = targets.length > 0 && targets.every(t => (t.sizes?.length || 0) > 0);

  const handleDownload = async () => {
    // Clear any previous errors when starting a new download attempt
    actions.setError(null);

    // Check if user is authenticated before allowing download
    if (!isAuthenticated) {
      setShowSignupPrompt(true);
      return;
    }

    // Credit check happens on the backend, but we show a warning UI
    try {
      await onDownload(logo.id, targets);
    } catch (err) {
      if (err.name === 'EmailNotVerifiedError') {
        setShowVerifyEmailModal(true);
      }
    }
  };

  const handleGetCredits = () => {
    navigate('/pricing');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 flex-shrink-0">
        <button
          onClick={onBack}
          className="btn-ghost px-2 py-1 text-slate-600 dark:text-slate-200"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
            />
          </svg>
          <span className="font-medium text-sm">Back</span>
        </button>
        <div className="divider-vertical" />
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-50">Download Logo</h2>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Logo preview */}
          <div className="glass-soft p-4">
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-violet-500/15 to-fuchsia-500/10 flex items-center justify-center border border-slate-200 dark:border-white/10 flex-shrink-0">
                {logo.url ? (
                  <img
                    src={logo.url}
                    alt="Selected logo"
                    className="w-full h-full object-cover rounded-xl"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-2xl flex items-center justify-center border border-slate-200 dark:border-white/10">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-slate-50">Logo Option {logo.id}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Download for one or more platforms</p>
              </div>
            </div>
          </div>

          {/* Platforms */}
          <div className="glass p-4">
            <div className="mb-3">
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-50">Platforms</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400">Select any platforms you want to ship on</p>
            </div>

            <div className="space-y-2">
              {DOWNLOAD_PLATFORMS.map((p) => (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition-all ${enabledPlatforms[p.id]
                    ? 'border-violet-400/30 bg-violet-500/10'
                    : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/15 hover:bg-slate-50 dark:hover:bg-white/5'
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={!!enabledPlatforms[p.id]}
                    onChange={() => togglePlatform(p.id)}
                    className="w-4 h-4 rounded border-white/20 bg-white/5 accent-violet-400 focus:ring-violet-400/60"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-slate-900 dark:text-slate-100">{p.label}</span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400 truncate">{p.description}</p>
                  </div>
                  {enabledPlatforms[p.id] && (
                    <svg className="w-5 h-5 text-violet-200 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Per-platform dimensions */}
          {DOWNLOAD_PLATFORMS.filter(p => enabledPlatforms[p.id]).map((p) => {
            const dimensions = DIMENSION_OPTIONS[p.id] || [];
            const selectedSizes = selectedSizesByPlatform[p.id] || [];

            return (
              <div key={p.id} className="glass p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-50">{p.label} Sizes</h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400">Choose which sizes to include</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => selectAll(p.id)}
                      className="px-2 py-1 text-xs text-violet-600 dark:text-violet-200 hover:text-violet-500 dark:hover:text-violet-100 font-semibold"
                    >
                      Select All
                    </button>
                    <span className="text-slate-200 dark:text-white/15">|</span>
                    <button
                      onClick={() => selectNone(p.id)}
                      className="px-2 py-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 font-semibold"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  {dimensions.map((dim) => (
                    <label
                      key={dim.size}
                      className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition-all ${selectedSizes.includes(dim.size)
                        ? 'border-violet-400/30 bg-violet-500/10'
                        : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/15 hover:bg-slate-50 dark:hover:bg-white/5'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSizes.includes(dim.size)}
                        onChange={() => toggleSize(p.id, dim.size)}
                        className="w-4 h-4 rounded border-white/20 bg-white/5 accent-violet-400 focus:ring-violet-400/60"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-slate-900 dark:text-slate-100">{dim.label}</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">{dim.size}</span>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 truncate">{dim.description}</p>
                      </div>
                      {selectedSizes.includes(dim.size) && (
                        <svg className="w-5 h-5 text-violet-200 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Credit info for authenticated users */}
          {isAuthenticated && (
            <div className={hasEnoughCredits ? 'alert-success' : 'alert-warn'}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center border ${hasEnoughCredits ? 'bg-emerald-500/15 border-emerald-400/20' : 'bg-amber-500/15 border-amber-400/20'}`}>
                    <svg className={`w-5 h-5 ${hasEnoughCredits ? 'text-green-600' : 'text-amber-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      {credits} credits available
                    </p>
                    <p className="text-xs opacity-80">
                      Download costs {downloadCost} credits
                    </p>
                  </div>
                </div>
                {!hasEnoughCredits && (
                  <button
                    onClick={handleGetCredits}
                    className="btn-secondary px-3 py-1.5 text-sm"
                  >
                    Get Credits
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Download button */}
          <div className="bg-violet-500/10 border border-violet-400/20 rounded-2xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-violet-900 dark:text-violet-100 text-sm">
                  {totalSelectedFiles} file{totalSelectedFiles !== 1 ? 's' : ''} selected
                </p>
                <p className="text-xs text-violet-700 dark:text-violet-200/80">
                  {canDownload
                    ? 'Ready to download as a single ZIP'
                    : 'Select at least one platform and at least one size for each'}
                </p>
              </div>
              <button
                onClick={handleDownload}
                disabled={!canDownload || (isAuthenticated && !hasEnoughCredits)}
                className={`${(!canDownload || (isAuthenticated && !hasEnoughCredits)) ? 'btn-secondary opacity-60' : 'btn-primary'} px-4 py-2`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                {isAuthenticated ? `Download (${downloadCost} credits)` : 'Download ZIP'}
              </button>
            </div>
          </div>

          {/* Guest notice */}
          {!isAuthenticated && (
            <div className="alert-warn">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-amber-200 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-medium">Account required for download</p>
                  <p className="text-xs opacity-80 mt-1">
                    Create a free account to download your logos. Your work will be saved to your account.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <SignupPrompt
        isOpen={showSignupPrompt}
        onClose={() => setShowSignupPrompt(false)}
        message="Create a free account to download your logos in all sizes."
      />

      <VerifyEmailModal
        isOpen={showVerifyEmailModal}
        onClose={() => setShowVerifyEmailModal(false)}
      />
    </div>
  );
}
